/** Grid presets — saved data-grid views (columns + order, filters, sort).
 *
 * One hook drives every grid family. State model:
 *
 * - PRESET mode: `?preset=<id>` is the whole covered state. The hook fetches
 *   the preset detail and layers its settings as a FALLBACK read source under
 *   the URL (`getParam`/`presetParams`); columns and sort come out as
 *   controlled models. Loading a preset is just a URL rewrite.
 * - Divergence: every write that touches covered state funnels through
 *   `beforeUrlWrite(next)`. In preset mode it materializes the full effective
 *   state into `next` (cols/ordering/filter params), deletes `preset`, and
 *   remembers the source preset IN MEMORY (`custom-diverged`) so the save
 *   dialog can offer "Update '<name>'" vs "Save as new". A reload of a custom
 *   URL loses provenance by design.
 * - CUSTOM/IDLE: the URL carries plain state; the hook only decodes it.
 *
 * `pivot` is reserved in the settings schema and never captured or applied
 * (DataGrid Pro has no pivoting).
 */
import type {
  GridColumnVisibilityModel,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import * as presetsApi from '@/api/presets';
import {
  parseSettings,
  type GridCodename,
  type GridPresetDetail,
  type PresetSettingsV1,
} from '@/api/presets';
import { useOptionalAuth } from '@/auth/AuthProvider';
import { useGridDefaultPreset } from '@/hooks/useUserPreferences';
import {
  defaultColumnState,
  orderingToSortModel,
  reconcileColumns,
  sortModelToOrdering,
  stateFromColsParam,
  visibleCols,
  type ColumnState,
} from '@/components/DataGrid/columnOrder';
import { useSnackbar } from '@/components/Snackbar';
import type { FilterDef, GridFiltersPresetLayer } from '@/hooks/useGridFilters';

/** How a grid family's filter state maps to the preset settings and the URL. */
export interface PresetFiltersAdapter {
  /** URL params that carry this grid's filter state (covered by presets). */
  urlParams: string[];
  /** Effective params → `settings.filters` JSON (undefined = nothing to save). */
  capture: (get: (param: string) => string | null) => unknown | undefined;
  /** `settings.filters` JSON → {param: value} for URL materialization. */
  toUrlParams: (filters: unknown) => Record<string, string>;
}

/** ResourceDataGrid-family adapter: filters are the page's URL filter params,
 *  stored as `{params: {...}}`. */
export function makeResourceFiltersAdapter(
  defs: FilterDef[],
  extraParams: string[] = [],
): PresetFiltersAdapter {
  const urlParams = [...defs.map((f) => f.param), ...extraParams];
  return {
    urlParams,
    capture: (get) => {
      const params: Record<string, string> = {};
      for (const p of urlParams) {
        const v = get(p);
        if (v) params[p] = v;
      }
      return Object.keys(params).length ? { params } : undefined;
    },
    toUrlParams: (filters) => {
      const params = (filters as { params?: Record<string, unknown> } | null)?.params;
      if (!params || typeof params !== 'object') return {};
      const out: Record<string, string> = {};
      for (const [k, v] of Object.entries(params)) {
        if (urlParams.includes(k) && typeof v === 'string' && v) out[k] = v;
      }
      return out;
    },
  };
}

export type PresetMode =
  | { kind: 'idle' }
  | { kind: 'preset'; presetId: number }
  | { kind: 'custom-diverged'; from: { id: number; name: string; ownedByMe: boolean } };

/** Everything PresetsMenu/SavePresetDialog need, threaded through the grid
 *  toolbar's slotProps. */
export interface PresetsToolbarConfig {
  grid: GridCodename;
  mode: PresetMode;
  /** The preset this view is based on, for the toolbar name chip: the loaded
   *  preset in preset mode, or the diverged-from preset (`modified: true`)
   *  while the session still remembers it. Null in a pristine/idle view or
   *  while the preset detail is still loading. */
  active: { id: number; name: string; modified: boolean } | null;
  supports: { columns: boolean; filters: boolean; sort: boolean };
  /** Build the settings blob for the checked save-dialog sections. */
  capture: (sections: { columns: boolean; filters: boolean; sort: boolean }) => PresetSettingsV1;
  /** Load a preset: URL becomes `?preset=<id>` (covered params dropped). */
  loadPreset: (id: number) => void;
  /** Back to the pristine default view (used after deleting the active preset). */
  clearPreset: () => void;
  /** "Update '<name>'" target — the active/diverged-from preset when owned by me. */
  updateTarget: { id: number; name: string } | null;
  /** After a successful POST/PATCH: cache detail, refresh lists, enter preset mode. */
  onSaved: (preset: GridPresetDetail) => void;
  /** The preset id saved as this grid's default view (auto-loaded on a pristine
   *  URL), or null. */
  defaultPresetId: number | null;
  /** Persist a preset as this grid's default view (a user preference). */
  setAsDefault: (presetId: number) => void;
  /** Clear this grid's default view. */
  clearDefault: () => void;
}

export interface UseGridPresetsOptions {
  grid: GridCodename;
  /** Base columns' fields in default order. */
  defaultOrder: string[];
  /** Fields hidden by default (the old initialState visibility map). */
  defaultHidden?: string[];
  /** Default DRF ordering string (mirrors the page's defaultSort). */
  defaultOrdering?: string;
  filters?: PresetFiltersAdapter;
  supportsSort?: boolean;
  /** false for embedded grid usages (no presets UI, URL untouched). */
  enabled?: boolean;
}

export interface UseGridPresets {
  mode: PresetMode;
  /** True while `?preset=` is set but its settings haven't loaded — render a
   *  spinner instead of the grid to avoid a default-state fetch flash. */
  blocking: boolean;
  /** Layered read (URL ?? loaded preset) for covered params. */
  getParam: (param: string) => string | null;
  /** Divergence choke point — first call on every covered-state URL write. */
  beforeUrlWrite: (next: URLSearchParams) => void;
  /** Plumb into useGridFilters as its presetLayer. */
  filtersLayer: GridFiltersPresetLayer;

  // Columns (controlled)
  orderedFields: string[];
  columnVisibilityModel: GridColumnVisibilityModel;
  onColumnOrderChange: (orderedFields: string[]) => void;
  onColumnVisibilityModelChange: (m: GridColumnVisibilityModel) => void;

  // Sort (controlled)
  sortModel: GridSortModel;
  onSortModelChange: (m: GridSortModel) => void;
  /** Effective DRF ordering string for the API query. */
  ordering: string;

  toolbar: PresetsToolbarConfig;
}

export function useGridPresets(options: UseGridPresetsOptions): UseGridPresets {
  const {
    grid,
    defaultOrder,
    defaultHidden = [],
    defaultOrdering = '',
    filters: adapter,
    supportsSort = true,
    enabled = true,
  } = options;

  const [sp, setSp] = useSearchParams();
  const auth = useOptionalAuth();
  const snackbar = useSnackbar();
  const qc = useQueryClient();

  // Saved default view for this grid (a user preference). Auto-applied on first
  // mount when the URL carries no explicit view (see the auto-load effect).
  const gridDefault = useGridDefaultPreset(grid);
  const didAutoLoadRef = useRef(false);

  const presetIdRaw = enabled ? sp.get('preset') : null;
  const presetId = presetIdRaw && /^\d+$/.test(presetIdRaw) ? Number(presetIdRaw) : null;

  const [divergedFrom, setDivergedFrom] = useState<{
    id: number;
    name: string;
    ownedByMe: boolean;
  } | null>(null);

  const detailQuery = useQuery({
    queryKey: ['gridPreset', presetId],
    queryFn: () => presetsApi.retrieve(presetId as number),
    enabled: presetId !== null,
    staleTime: 60_000,
    retry: false,
  });
  const detail = presetId !== null ? detailQuery.data : undefined;
  // A preset from another grid must never apply here (spec: presets are
  // grid-scoped). Treat it like a broken link.
  const wrongGrid = detail !== undefined && detail.grid !== grid;

  const dropPresetParam = useCallback(() => {
    setSp(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('preset');
        return next;
      },
      { replace: true },
    );
  }, [setSp]);

  useEffect(() => {
    if (presetId === null) return;
    if (detailQuery.isError) {
      snackbar.show('That preset no longer exists or is not visible to you.', 'warning');
      dropPresetParam();
      // A stale default heals itself — drop it so it stops auto-loading.
      if (gridDefault.defaultPresetId === presetId) gridDefault.clearDefault();
    } else if (wrongGrid) {
      snackbar.show(`Preset #${presetId} belongs to another grid.`, 'warning');
      dropPresetParam();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailQuery.isError, wrongGrid, presetId]);

  const settings: PresetSettingsV1 | null = useMemo(
    () => (detail && !wrongGrid ? parseSettings(detail.settings) : null),
    [detail, wrongGrid],
  );

  // An embedded grid (`enabled: false`) leaves the URL to its host page, so its own
  // column and sort choices live here instead: component state, never persisted,
  // in the same `cols` / `ordering` encoding the URL would carry. (Without this a
  // controlled embedded grid ignored every column toggle and header sort.)
  const [localCols, setLocalCols] = useState<string | null>(null);
  const [localOrdering, setLocalOrdering] = useState<string | null>(null);

  // ---- effective column state ---------------------------------------------
  const colsParam = enabled ? sp.get('cols') : localCols;
  const columnState: ColumnState = useMemo(() => {
    if (colsParam) return stateFromColsParam(colsParam, defaultOrder);
    if (settings?.columns) return reconcileColumns(settings.columns, defaultOrder, defaultHidden);
    return defaultColumnState(defaultOrder, defaultHidden);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colsParam, settings, defaultOrder.join(','), defaultHidden.join(',')]);

  // ---- effective ordering --------------------------------------------------
  const ordering =
    (enabled ? sp.get('ordering') : localOrdering) ??
    (supportsSort ? settings?.ordering : undefined) ??
    defaultOrdering;
  // Only grid-known fields go into the CONTROLLED model — the API `ordering`
  // string may legitimately reference non-column fields, and handing those to
  // DataGridPro makes it sanitize the model and fire onSortModelChange at
  // mount (a phantom "user change" that would falsely diverge a preset).
  const sortModel = useMemo(
    () => orderingToSortModel(ordering).filter((s) => defaultOrder.includes(s.field)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ordering, defaultOrder.join(',')],
  );

  // ---- layered filter reads ------------------------------------------------
  const presetParams = useMemo(() => {
    if (!settings) return null;
    const p = new URLSearchParams();
    if (adapter && settings.filters !== undefined) {
      for (const [k, v] of Object.entries(adapter.toUrlParams(settings.filters))) p.set(k, v);
    }
    if (supportsSort && settings.ordering) p.set('ordering', settings.ordering);
    return p;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings, adapter]);

  const getParam = useCallback(
    (param: string) => sp.get(param) ?? presetParams?.get(param) ?? null,
    [sp, presetParams],
  );

  // ---- divergence ----------------------------------------------------------
  const beforeUrlWrite = useCallback(
    (next: URLSearchParams) => {
      if (!enabled || !next.has('preset')) return;
      next.delete('preset');
      // Materialize the full effective covered state so the custom URL is
      // self-contained (the preset layer is about to disappear).
      if (adapter) {
        for (const p of adapter.urlParams) {
          const v = sp.get(p) ?? presetParams?.get(p);
          if (v) next.set(p, v);
          else next.delete(p);
        }
      }
      if (supportsSort && ordering) next.set('ordering', ordering);
      next.set('cols', visibleCols(columnState.order, columnState.visibility).join(','));
      if (detail && !wrongGrid) {
        setDivergedFrom({
          id: detail.id,
          name: detail.name,
          ownedByMe: auth?.user != null && detail.owner.id === auth.user.id,
        });
      }
    },
    [enabled, adapter, sp, presetParams, supportsSort, ordering, columnState, detail, wrongGrid, auth],
  );

  const filtersLayer = useMemo<GridFiltersPresetLayer>(
    () => ({ presetParams, beforeWrite: beforeUrlWrite }),
    [presetParams, beforeUrlWrite],
  );

  // ---- covered-state write helpers ----------------------------------------
  const writeCols = useCallback(
    (order: string[], visibility: GridColumnVisibilityModel) => {
      if (!enabled) {
        setLocalCols(visibleCols(order, visibility).join(','));
        return;
      }
      setSp(
        (prev) => {
          const next = new URLSearchParams(prev);
          beforeUrlWrite(next);
          next.set('cols', visibleCols(order, visibility).join(','));
          return next;
        },
        { replace: true },
      );
    },
    [enabled, setSp, beforeUrlWrite],
  );

  const onColumnOrderChange = useCallback(
    (orderedFields: string[]) => {
      // Echo guard (same reasoning as onSortModelChange below): DataGridPro
      // re-emits the order it was handed in some initialization flows, and
      // treating that echo as a user drag silently diverges a loaded preset
      // (?preset vanishes from the URL). The emitted list may be either the
      // full stored order or just its visible projection — a genuine drag
      // differs from both.
      const emitted = orderedFields.join(',');
      if (
        emitted === columnState.order.join(',') ||
        emitted === visibleCols(columnState.order, columnState.visibility).join(',')
      ) {
        return;
      }
      writeCols(orderedFields, columnState.visibility);
    },
    [writeCols, columnState],
  );

  const onColumnVisibilityModelChange = useCallback(
    (m: GridColumnVisibilityModel) => {
      // Echo guard: compare effective HIDDEN sets — at init the grid can hand
      // back a model with a different key set but identical meaning, and only
      // hidden-ness feeds the cols= projection.
      const hiddenOf = (v: GridColumnVisibilityModel) =>
        Object.keys(v)
          .filter((k) => v[k] === false)
          .sort()
          .join(',');
      if (hiddenOf(m) === hiddenOf(columnState.visibility)) return;
      writeCols(columnState.order, m);
    },
    [writeCols, columnState],
  );

  const onSortModelChange = useCallback(
    (m: GridSortModel) => {
      // Echo guard: the grid re-emits the model it was handed in some flows —
      // only a genuinely different sort counts as a user change.
      if (sortModelToOrdering(m) === sortModelToOrdering(sortModel)) return;
      if (!enabled) {
        // Cleared → back to the default ordering, as a URL without `ordering` is.
        setLocalOrdering(sortModelToOrdering(m) || null);
        return;
      }
      setSp(
        (prev) => {
          const next = new URLSearchParams(prev);
          beforeUrlWrite(next);
          const ord = sortModelToOrdering(m);
          if (ord) next.set('ordering', ord);
          else next.delete('ordering');
          return next;
        },
        { replace: true },
      );
    },
    [enabled, setSp, beforeUrlWrite, sortModel],
  );

  // ---- preset load/save ----------------------------------------------------
  const coveredParams = useMemo(
    () => ['cols', 'ordering', ...(adapter?.urlParams ?? [])],
    [adapter],
  );

  const loadPreset = useCallback(
    (id: number, opts?: { replace?: boolean }) => {
      setSp(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const p of coveredParams) next.delete(p);
          next.set('preset', String(id));
          return next;
        },
        opts?.replace ? { replace: true } : undefined,
      );
      setDivergedFrom(null);
    },
    [setSp, coveredParams],
  );

  // A view is "pristine" when the URL carries no preset and none of the covered
  // params — only then does a saved default apply ("if the URL overrides the
  // preset, ignore the default").
  const urlPristine = useMemo(() => {
    if (!enabled) return false;
    if (sp.get('preset')) return false;
    return !coveredParams.some((p) => sp.get(p));
  }, [enabled, sp, coveredParams]);

  // Auto-load the saved default once, on first mount, when the URL is pristine.
  useEffect(() => {
    if (!enabled || didAutoLoadRef.current) return;
    if (!urlPristine) { didAutoLoadRef.current = true; return; }
    if (gridDefault.isLoading) return; // wait for the preference to load
    didAutoLoadRef.current = true;
    if (gridDefault.defaultPresetId != null) {
      loadPreset(gridDefault.defaultPresetId, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, urlPristine, gridDefault.isLoading, gridDefault.defaultPresetId]);

  const clearPreset = useCallback(() => {
    setSp(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const p of coveredParams) next.delete(p);
        next.delete('preset');
        return next;
      },
      { replace: true },
    );
    setDivergedFrom(null);
  }, [setSp, coveredParams]);

  const capture = useCallback(
    (sections: { columns: boolean; filters: boolean; sort: boolean }): PresetSettingsV1 => {
      const s: PresetSettingsV1 = { version: 1 };
      if (sections.columns) {
        s.columns = { order: columnState.order, visibility: columnState.visibility };
      }
      if (sections.filters && adapter) {
        const f = adapter.capture(getParam);
        if (f !== undefined) s.filters = f;
      }
      if (sections.sort && supportsSort && ordering) s.ordering = ordering;
      return s;
    },
    [columnState, adapter, getParam, supportsSort, ordering],
  );

  const onSaved = useCallback(
    (preset: GridPresetDetail) => {
      qc.setQueryData(['gridPreset', preset.id], preset);
      void qc.invalidateQueries({ queryKey: ['gridPresets', grid] });
      void qc.invalidateQueries({ queryKey: ['gridPresetsAll'] });
      loadPreset(preset.id);
    },
    [qc, grid, loadPreset],
  );

  // ---- mode / toolbar ------------------------------------------------------
  const mode: PresetMode = useMemo(
    () =>
      presetId !== null
        ? { kind: 'preset', presetId }
        : divergedFrom
          ? { kind: 'custom-diverged', from: divergedFrom }
          : { kind: 'idle' },
    [presetId, divergedFrom],
  );

  const updateTarget = useMemo(() => {
    if (mode.kind === 'custom-diverged' && mode.from.ownedByMe) {
      return { id: mode.from.id, name: mode.from.name };
    }
    if (
      mode.kind === 'preset' &&
      detail &&
      !wrongGrid &&
      auth?.user != null &&
      detail.owner.id === auth.user.id
    ) {
      return { id: detail.id, name: detail.name };
    }
    return null;
  }, [mode, detail, wrongGrid, auth]);

  const active = useMemo(() => {
    if (mode.kind === 'preset' && detail && !wrongGrid) {
      return { id: detail.id, name: detail.name, modified: false };
    }
    if (mode.kind === 'custom-diverged') {
      return { id: mode.from.id, name: mode.from.name, modified: true };
    }
    return null;
  }, [mode, detail, wrongGrid]);

  const toolbar = useMemo<PresetsToolbarConfig>(
    () => ({
      grid,
      mode,
      active,
      supports: { columns: true, filters: adapter !== undefined, sort: supportsSort },
      capture,
      loadPreset,
      clearPreset,
      updateTarget,
      onSaved,
      defaultPresetId: gridDefault.defaultPresetId,
      setAsDefault: gridDefault.setDefault,
      clearDefault: gridDefault.clearDefault,
    }),
    [
      grid,
      mode,
      active,
      adapter,
      supportsSort,
      capture,
      loadPreset,
      clearPreset,
      updateTarget,
      onSaved,
      gridDefault.defaultPresetId,
      gridDefault.setDefault,
      gridDefault.clearDefault,
    ],
  );

  // Block the grid render while a saved default is still resolving on a pristine
  // URL — avoids a flash of the default view before it jumps to the preset.
  const awaitingAutoLoad =
    enabled && !didAutoLoadRef.current && urlPristine &&
    (gridDefault.isLoading || gridDefault.defaultPresetId != null);

  return {
    mode,
    blocking: (presetId !== null && detail === undefined && !detailQuery.isError) || awaitingAutoLoad,
    getParam,
    beforeUrlWrite,
    filtersLayer,
    orderedFields: columnState.order,
    columnVisibilityModel: columnState.visibility,
    onColumnOrderChange,
    onColumnVisibilityModelChange,
    sortModel,
    onSortModelChange,
    ordering,
    toolbar,
  };
}
